import { ArrowUpRight, LogIn, ShieldCheck, Zap } from 'lucide-react'
import { Helmet } from 'react-helmet'

import blogImg1 from '@/assets/marketing/images/blog-img-1.png'
import blogImg2 from '@/assets/marketing/images/blog-img-2.png'
import blogImgLong from '@/assets/marketing/images/blog-img-long-temp.png'
import safetyVestImg from '@/assets/marketing/images/safety-vest-truck-wheel.png'
import truckHeroImg from '@/assets/marketing/images/truck-insurance-hero.jpg'
import { Button } from '@/components/ui/button'
import { appHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { BlogCard } from './components/BlogCard'
import { Checklist } from './components/Checklist'
import { HomeEstimateBanner } from './components/HomeEstimateBanner'
import { MarketingCarrierLogos } from './components/MarketingCarrierLogos'
import { ProductsSection } from './components/ProductsSection'
import { Reveal } from './components/Reveal'
import { ValueProp } from './components/ValueProp'
import { COVERAGES } from './content/coverages'

const VALUE_PROPS = [
  {
    key: 'competitive-quotes',
    icon: <Zap />,
    title: (
      <>
        Competitive quotes,
        <br />
        fast
      </>
    ),
    body: 'We work with top insurance carriers to find the best quotes for your business.',
  },
  {
    key: 'safety-compliance',
    icon: <ShieldCheck />,
    title: 'Year-round safety & compliance support',
    body: 'We flag risks before they hit your premiums, not after.',
  },
  {
    key: 'policy-management',
    icon: <LogIn />,
    title: 'Seamless policy management',
    body: 'We make it easy to get what you need to keep moving.',
  },
]

const BLOG_POSTS = [
  {
    size: 'small' as const,
    title: 'What insurance do you need to start a trucking company?',
    href: 'https://blog.cai.ai/what-insurance-do-you-need-to-start-a-trucking-company',
    imageSrc: blogImg1,
  },
  {
    size: 'small' as const,
    title: 'How does your CSA score affect your trucking insurance?',
    href: 'https://blog.cai.ai/how-does-your-csa-score-affect-your-trucking-insurance',
    imageSrc: blogImg2,
  },
  {
    size: 'large' as const,
    title: "What's the difference between Auto Liability, Cargo, and General Liability coverage?",
    href: 'https://blog.cai.ai/whats-the-difference-between-auto-liability-cargo-and-general-liability-coverage',
    imageSrc: blogImgLong,
  },
]

export function Insurance() {
  return (
    <>
      <Helmet>
        <title>Commercial Trucking Insurance</title>
      </Helmet>
      <meta
        name="description"
        content="Shop auto liability, cargo, physical damage, and more coverage for your fleet. Get quotes from A-rated carriers with help from Cai."
      />

      <section id="hero" className="relative -mt-15.5 overflow-hidden pt-15.5">
        <img
          src={truckHeroImg}
          alt=""
          className="absolute inset-0 z-0 h-full w-full object-cover object-bottom"
        />
        <div className="flex min-h-[clamp(420px,60vh,560px)] items-center justify-center lg:min-h-[clamp(560px,68vh,620px)]">
          <div className="relative z-1 flex flex-col items-center gap-4 px-5.5 text-center">
            <Reveal className="flex flex-col items-center gap-4 duration-[900ms]" delay={1200}>
              <span className="eyebrow hero-eyebrow">AI-powered insurance</span>
              <h1 className="h1-hero max-w-225">
                Smarter quoting.
                <br />
                Real savings.
              </h1>
            </Reveal>
            <Reveal className="duration-[900ms]" delay={1700}>
              <div className="mt-10 flex flex-col items-center gap-2">
                <Button
                  size="lg"
                  href={appHref('/application/start')}
                  className="shadow-[0_4px_12px_rgba(0,0,0,0.15)]"
                  onClick={() =>
                    trackMarketingCta('insurance_hero_get_quotes', {
                      destination: appHref('/application/start'),
                    })
                  }
                >
                  Get your quotes
                </Button>
                <span className="text-ink-400 font-mono text-xs">
                  Apply in minutes, no requirement to buy.
                </span>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal className="relative z-1 duration-[900ms]" delay={2200}>
          <MarketingCarrierLogos heading="Shop the nation's leading commercial insurance carriers" />
        </Reveal>
      </section>

      <section id="coverages" className="mx-auto max-w-(--maxw) px-5.5 py-24">
        <Reveal>
          <span className="eyebrow">Coverages</span>
          <div className="grid grid-cols-3 items-start gap-4 max-md:grid-cols-1">
            <div className="col-span-1 flex flex-col gap-8">
              <h2 className="h3 mt-6">One broker for all your fleet&apos;s needs</h2>
              <Checklist className="max-md:mb-6 [&_li]:text-xl" items={COVERAGES} />
            </div>
            <div className="relative col-span-2 aspect-4/3 overflow-hidden rounded-sm max-md:col-span-1">
              <img
                src={safetyVestImg}
                alt="Safety vest hanging by a truck wheel"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="mt-20 flex flex-col items-start gap-3.5">
            <h2 className="h3">Intelligent trucking insurance</h2>
            <a
              href="https://blog.cai.ai/how-cai-is-using-ai-to-fix-trucking-insurance"
              className="text-ink-500 hover:text-ink border-ink-500/35 hover:border-ink border-b pb-0.5 text-lg no-underline transition-colors duration-150"
            >
              Learn more about how Cai uses AI.
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 border border-white max-md:grid-cols-1">
            {VALUE_PROPS.map((prop) => (
              <ValueProp
                key={prop.key}
                icon={prop.icon}
                title={prop.title}
                className="border-r border-white px-8 py-10 last:border-r-0 max-md:border-r-0 max-md:border-b max-md:last:border-b-0"
              >
                {prop.body}
              </ValueProp>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="bg-brand flex justify-center px-5.5 py-24">
        <Reveal className="text-ink max-w-225 text-center">
          <h2 className="h2">
            <span className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <span className="text-[32px] leading-[1.1] tracking-[-0.02em]">Easy application.</span>
              <span className="text-[32px] leading-[1.1] tracking-[-0.02em]">A-rated insurers.</span>
              <span className="text-[32px] leading-[1.1] tracking-[-0.02em]">Instant COI.</span>
            </span>
            <span className="mt-6 block">
              Cai is built to move fast
              <br />
              so you never lose a load.
            </span>
          </h2>
        </Reveal>
      </section>

      <ProductsSection />

      <section id="blog" className="bg-surface py-24">
        <div className="mx-auto max-w-(--maxw) px-5.5">
          <Reveal>
            <span className="eyebrow">Blog</span>
            <div className="mt-6 grid grid-cols-3 gap-4 max-lg:grid-cols-1">
              <div className="col-span-1 flex flex-col gap-6">
                <h3 className="h3">Lessons from running our own 600+ truck fleet</h3>
                <p className="text-md text-ink-500 leading-[1.55]">
                  We&apos;re sharing what we&apos;ve learned along the way, so trucking can run smarter and
                  spend less.
                </p>
                <Button
                  variant="outline"
                  size="lg"
                  href="https://blog.cai.ai"
                  className="self-start"
                  onClick={() =>
                    trackMarketingCta('insurance_blog_handbook', { destination: 'https://blog.cai.ai' })
                  }
                >
                  Read the Operators Handbook
                  <ArrowUpRight aria-hidden="true" />
                </Button>
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-4 max-lg:col-span-1 max-lg:grid-cols-1">
                {BLOG_POSTS.map((post) => (
                  <BlogCard
                    key={post.href}
                    size={post.size}
                    title={post.title}
                    href={post.href}
                    imageSrc={post.imageSrc}
                    onClick={() =>
                      trackMarketingCta('insurance_blog_post', { title: post.title, destination: post.href })
                    }
                  />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <HomeEstimateBanner />
    </>
  )
}
